// Tranche CA: resident self-service home at /me/home.
//
// Renders the caller's ResidentProfile (with household + unit + building),
// their active + recent guest passes, and a "Sponsor a guest" modal. All
// queries are caller-scoped server-side — there is no `x-tenant-context`
// branching, the resident's JWT carries their scope.
//
// T4: covers Stitch v2 `resident_home_multi_family_mobile_focus_1` +
// `_2` via responsive layout — the top-level grid is `md:grid-cols-2`
// (single-column < 768 px) and the inner detail-list grids now stack
// at `< 640 px`. Same data, no separate mobile component.
//
// Out of scope for this PR (separate Tranche CA follow-ups):
//   - Resident credentials list (no /me/credentials endpoint yet)
//   - Recent activity stream (no /me/events endpoint yet)
//   - QR / PIN display on guest pass create (controller does not yet
//     return a portal-renderable artifact; the backend stores the
//     guest_pass row and credential issuance is a follow-up)

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, CardBody, Icon, Input, Label, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError, newIdempotencyKey } from '../lib/api.js';

interface BuildingLite {
  id: string;
  name: string;
  address: string | null;
}

interface UnitLite {
  id: string;
  unit_number: string;
  floor: string | null;
  building: BuildingLite | null;
}

interface HouseholdLite {
  id: string;
  label: string | null;
  moved_in_at: string;
  moved_out_at: string | null;
  unit: UnitLite | null;
}

interface ResidentProfile {
  id: string;
  tenant_id: string;
  household_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  relationship: 'occupant' | 'head_of_household' | 'spouse' | 'child' | 'guest';
  household: HouseholdLite | null;
}

interface GuestPassRow {
  id: string;
  sponsor_profile_id: string;
  guest_name: string;
  guest_email: string | null;
  valid_from: string;
  valid_until: string;
  credential_id: string | null;
}

interface CreateModalState {
  guestName: string;
  guestEmail: string;
  validFromLocal: string;
  validUntilLocal: string;
  busy: boolean;
  error: string | null;
  idempotencyKey: string;
}

function isoFromLocal(localDateTime: string): string {
  // <input type="datetime-local"> returns YYYY-MM-DDTHH:MM without tz.
  // Treat it as local time and round-trip through Date for ISO.
  return new Date(localDateTime).toISOString();
}

function localFromIso(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * UX-008: this read only the time window, so a pass with no credential issued
 * showed a green "Active" to the resident while their guest could not open
 * anything. `credential_id` was on the row all along and never consulted.
 *
 * Being inside the window and having a credential are two facts, and the
 * resident needs both.
 */
function statusOf(gp: GuestPassRow): {
  label: string;
  tone: 'success' | 'warning' | 'neutral' | 'danger';
  detail?: string;
} {
  const now = Date.now();
  const from = new Date(gp.valid_from).getTime();
  const until = new Date(gp.valid_until).getTime();
  const issued = Boolean(gp.credential_id);

  if (now > until) return { label: 'Expired', tone: 'neutral' };

  if (now < from) {
    return issued
      ? { label: 'Scheduled', tone: 'neutral' }
      : {
          label: 'Scheduled — key pending',
          tone: 'warning',
          detail: 'A key still has to be issued before this pass starts.',
        };
  }

  if (!issued) {
    return {
      label: 'Not yet usable',
      tone: 'danger',
      detail:
        'The time window is open but no key has been issued yet, so your guest cannot get in. Contact the property office.',
    };
  }

  const hoursLeft = (until - now) / (60 * 60 * 1000);
  if (hoursLeft < 2) return { label: 'Active · ending soon', tone: 'warning' };
  return { label: 'Active', tone: 'success' };
}

export function ResidentHomePage() {
  const [profile, setProfile] = useState<ResidentProfile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [guestPasses, setGuestPasses] = useState<GuestPassRow[] | null>(null);
  const [passesError, setPassesError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [createModal, setCreateModal] = useState<CreateModalState | null>(null);
  const [revoking, setRevoking] = useState<{
    row: GuestPassRow;
    busy: boolean;
    error: string | null;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setProfileError(null);
    apiClient
      .get<{ data: ResidentProfile }>('/api/v1/me/profile')
      .then((r) => {
        if (cancelled) return;
        setProfile(r.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setProfileError(err instanceof ApiError ? err.message : 'Could not load your profile.');
      });
    setPassesError(null);
    apiClient
      .get<{ data: GuestPassRow[] }>('/api/v1/me/guest-passes')
      .then((r) => {
        if (cancelled) return;
        setGuestPasses(r.data);
        setPassesError(null);
      })
      .catch((err: unknown) => {
        // UX-019: this used to store [] on failure, so "we could not load your
        // guest passes" and "you have no guest passes" rendered identically —
        // and the reassuring one was the lie.
        if (cancelled) return;
        setGuestPasses([]);
        setPassesError(
          err instanceof ApiError ? err.message : 'Could not load your guest passes.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  const { active, scheduled, expired } = useMemo(() => {
    const a: GuestPassRow[] = [];
    const s: GuestPassRow[] = [];
    const e: GuestPassRow[] = [];
    const now = Date.now();
    for (const gp of guestPasses ?? []) {
      const from = new Date(gp.valid_from).getTime();
      const until = new Date(gp.valid_until).getTime();
      if (now > until) e.push(gp);
      else if (now < from) s.push(gp);
      else a.push(gp);
    }
    return { active: a, scheduled: s, expired: e };
  }, [guestPasses]);

  function openCreate(): void {
    const now = new Date();
    const startInOneHour = new Date(now.getTime() + 60 * 60 * 1000);
    const endAfterFourHours = new Date(now.getTime() + 5 * 60 * 60 * 1000);
    setCreateModal({
      guestName: '',
      guestEmail: '',
      validFromLocal: localFromIso(startInOneHour.toISOString()),
      validUntilLocal: localFromIso(endAfterFourHours.toISOString()),
      busy: false,
      error: null,
      idempotencyKey: newIdempotencyKey(),
    });
  }

  async function submitCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!createModal || !profile) return;
    if (new Date(createModal.validUntilLocal) <= new Date(createModal.validFromLocal)) {
      setCreateModal({
        ...createModal,
        error: 'Valid-until must be after valid-from.',
      });
      return;
    }
    setCreateModal({ ...createModal, busy: true, error: null });
    try {
      await apiClient.post(
        '/api/v1/me/guest-passes',
        {
          sponsor_profile_id: profile.id,
          guest_name: createModal.guestName.trim(),
          guest_email: createModal.guestEmail.trim() ? createModal.guestEmail.trim() : null,
          valid_from: isoFromLocal(createModal.validFromLocal),
          valid_until: isoFromLocal(createModal.validUntilLocal),
        },
        {
          headers: { 'Idempotency-Key': createModal.idempotencyKey },
        },
      );
      setCreateModal(null);
      setToast(`Guest pass created for ${createModal.guestName.trim()}.`);
      reload();
    } catch (err: unknown) {
      setCreateModal({
        ...createModal,
        busy: false,
        error: err instanceof ApiError ? err.message : 'Could not create the guest pass.',
      });
    }
  }

  async function submitRevoke(): Promise<void> {
    if (!revoking) return;
    setRevoking({ ...revoking, busy: true, error: null });
    try {
      await apiClient.delete(`/api/v1/me/guest-passes/${revoking.row.id}`);
      const guestName = revoking.row.guest_name;
      setRevoking(null);
      setToast(`Revoked guest pass for ${guestName}.`);
      reload();
    } catch (err: unknown) {
      setRevoking({
        ...revoking,
        busy: false,
        error: err instanceof ApiError ? err.message : 'Could not revoke the guest pass.',
      });
    }
  }

  if (profileError) {
    return (
      <div className="space-y-4">
        <Card>
          <CardBody>
            <p className="font-body-md text-body-md text-error" role="alert">
              {profileError}
            </p>
            <p className="mt-2 font-body-md text-body-md text-on-surface-variant">
              If you just accepted an invite, ask your property manager to confirm your
              resident profile is linked to this account.
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-primary-container p-2 text-on-primary">
            <Icon name="home" />
          </span>
          <div>
            <h1 className="font-headline-sm text-headline-sm text-on-surface">
              {profile ? `Welcome, ${profile.name}` : 'Welcome'}
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Your home for guest passes, building info, and your recent activity.
            </p>
          </div>
        </div>
      </header>

      {/* UX-001: this page had no outgoing links at all. A resident signed in,
          landed here, and the only navigation on offer was the operator menu —
          every item of which failed server-side. These are the resident's own
          surfaces; the shell now lists them too, but a resident should not have
          to find the sidebar to get anywhere. */}
      <nav aria-label="Your account" className="grid gap-3 sm:grid-cols-3">
        {[
          {
            href: '/me/credentials',
            icon: 'key',
            label: 'My keys',
            detail: 'Fobs, PINs and phone keys issued to you.',
          },
          {
            href: '/me/events',
            icon: 'history',
            label: 'My activity',
            detail: 'Where and when your keys were used.',
          },
          {
            href: '/me/amenities/reservations',
            icon: 'event_available',
            label: 'Amenity bookings',
            detail: 'Book a shared space or cancel a booking.',
          },
        ].map((link) => (
          <Link
            key={link.href}
            to={link.href}
            className="flex items-start gap-3 rounded-xl border border-outline-variant bg-surface-container-lowest p-4 hover:bg-surface-container-low focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Icon name={link.icon} className="text-on-surface-variant" />
            <span className="min-w-0">
              <span className="block font-title-sm text-title-sm text-on-surface">{link.label}</span>
              <span className="block font-body-sm text-body-sm text-on-surface-variant">
                {link.detail}
              </span>
            </span>
          </Link>
        ))}
      </nav>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardBody>
            <h2 className="font-title-md text-title-md text-on-surface mb-2">Your household</h2>
            {!profile ? (
              <div
                role="status"
                aria-live="polite"
                className="flex items-center gap-2 text-on-surface-variant"
              >
                <Spinner />
                <span>Loading your details…</span>
              </div>
            ) : (
              <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-2 font-body-md text-body-md">
                <dt className="text-on-surface-variant">Building</dt>
                <dd className="col-span-2 text-on-surface">
                  {profile.household?.unit?.building?.name ?? '—'}
                </dd>
                <dt className="text-on-surface-variant">Unit</dt>
                <dd className="col-span-2 text-on-surface">
                  {profile.household?.unit?.unit_number ?? '—'}
                  {profile.household?.unit?.floor ? ` (floor ${profile.household.unit.floor})` : ''}
                </dd>
                <dt className="text-on-surface-variant">Household</dt>
                <dd className="col-span-2 text-on-surface">
                  {profile.household?.label ?? `#${profile.household_id.slice(0, 8)}…`}
                </dd>
                <dt className="text-on-surface-variant">Relationship</dt>
                {/* `capitalize` title-cases every word, which turned
                    head_of_household into "Head Of Household". Only the first
                    letter belongs in caps. */}
                <dd className="col-span-2 first-letter:uppercase text-on-surface">
                  {profile.relationship.replace(/_/g, ' ')}
                </dd>
                <dt className="text-on-surface-variant">Moved in</dt>
                <dd className="col-span-2 text-on-surface">
                  {profile.household
                    ? new Date(profile.household.moved_in_at).toLocaleDateString()
                    : '—'}
                </dd>
              </dl>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h2 className="font-title-md text-title-md text-on-surface mb-2">
              Your contact info
            </h2>
            {!profile ? (
              <div
                role="status"
                aria-live="polite"
                className="flex items-center gap-2 text-on-surface-variant"
              >
                <Spinner />
                <span>Loading your details…</span>
              </div>
            ) : (
              <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-2 font-body-md text-body-md">
                <dt className="text-on-surface-variant">Email</dt>
                <dd className="col-span-2 text-on-surface">{profile.email ?? '—'}</dd>
                <dt className="text-on-surface-variant">Phone</dt>
                <dd className="col-span-2 text-on-surface">{profile.phone ?? '—'}</dd>
              </dl>
            )}
            <p className="mt-3 font-body-md text-body-md text-on-surface-variant">
              Need to fix something here? Ask your property manager —
              you'll be able to edit your own contact info from this
              page in a future update.
            </p>
          </CardBody>
        </Card>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-title-lg text-title-lg text-on-surface">Your guest passes</h2>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Sponsor someone you'd like to grant temporary access. Passes are
              tied to your household.
            </p>
          </div>
          <Button
            variant="primary"
            onClick={openCreate}
            disabled={!profile}
            data-testid="sponsor-guest-btn"
          >
            <Icon name="add" className="text-sm" />
            <span className="ml-1">Sponsor a guest</span>
          </Button>
        </div>

        {guestPasses === null && (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center gap-2 text-on-surface-variant"
          >
            <Spinner />
            <span>Loading your guest passes…</span>
          </div>
        )}

        {/* UX-019: "we could not load these" and "you have none" are
            different facts and now say so. */}
        {passesError && (
          <Card>
            <CardBody>
              <p className="font-body-md text-body-md text-error" role="alert">
                {passesError}
              </p>
              <p className="mt-2 font-body-md text-body-md text-on-surface-variant">
                Any passes you have sponsored are unaffected — this page just
                could not read them. Try again in a moment.
              </p>
              <Button className="mt-3" variant="secondary" onClick={reload}>
                Try again
              </Button>
            </CardBody>
          </Card>
        )}

        {!passesError &&
          guestPasses !== null &&
          active.length === 0 &&
          scheduled.length === 0 &&
          expired.length === 0 && (
            <Card>
              <CardBody>
                <p className="font-body-md text-body-md text-on-surface-variant">
                  You haven't sponsored anyone yet. When you do, your
                  guests' passes show up here so you can keep track of
                  them or revoke one if your plans change. Click
                  <strong> Sponsor a guest</strong> to create your first pass.
                </p>
              </CardBody>
            </Card>
          )}

        {guestPasses !== null && (active.length > 0 || scheduled.length > 0 || expired.length > 0) && (
          <GuestPassTable
            active={active}
            scheduled={scheduled}
            expired={expired}
            onRevoke={(row) => setRevoking({ row, busy: false, error: null })}
          />
        )}
      </section>

      {createModal && profile && (
        <ModalShell title="Sponsor a guest" onClose={() => setCreateModal(null)}>
          <form onSubmit={submitCreate} className="space-y-4">
            <div>
              <Label htmlFor="gp-name">Guest name</Label>
              <Input
                id="gp-name"
                required
                maxLength={120}
                value={createModal.guestName}
                onChange={(e) =>
                  setCreateModal({ ...createModal, guestName: e.target.value })
                }
                placeholder="e.g. Maya Patel"
                data-testid="gp-name-input"
              />
            </div>
            <div>
              <Label htmlFor="gp-email">Guest email (optional)</Label>
              <Input
                id="gp-email"
                type="email"
                maxLength={254}
                value={createModal.guestEmail}
                onChange={(e) =>
                  setCreateModal({ ...createModal, guestEmail: e.target.value })
                }
                placeholder="maya@example.com"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="gp-from">Valid from</Label>
                <input
                  id="gp-from"
                  type="datetime-local"
                  required
                  value={createModal.validFromLocal}
                  onChange={(e) =>
                    setCreateModal({ ...createModal, validFromLocal: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
                />
              </div>
              <div>
                <Label htmlFor="gp-until">Valid until</Label>
                <input
                  id="gp-until"
                  type="datetime-local"
                  required
                  value={createModal.validUntilLocal}
                  onChange={(e) =>
                    setCreateModal({ ...createModal, validUntilLocal: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
                />
              </div>
            </div>
            {createModal.error && (
              <p className="font-body-md text-body-md text-error" role="alert">
                {createModal.error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCreateModal(null)}
                disabled={createModal.busy}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={createModal.busy}
                data-testid="gp-submit-btn"
              >
                {createModal.busy ? 'Creating…' : 'Create pass'}
              </Button>
            </div>
          </form>
        </ModalShell>
      )}

      {revoking && (
        <ModalShell
          title={`Revoke guest pass for ${revoking.row.guest_name}?`}
          onClose={() => setRevoking(null)}
        >
          <p className="font-body-md text-body-md text-on-surface">
            The guest will lose access right away. If they're already inside, their key
            stops working the next time they try a door.
          </p>
          {revoking.error && (
            <p className="mt-3 font-body-md text-body-md text-error" role="alert">
              {revoking.error}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRevoking(null)}
              disabled={revoking.busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={submitRevoke}
              disabled={revoking.busy}
            >
              {revoking.busy ? 'Revoking…' : 'Revoke'}
            </Button>
          </div>
        </ModalShell>
      )}

      {toast && (
        <div
          role="status"
          className="safe-area-pb fixed bottom-20 left-4 right-4 z-30 rounded-md bg-inverse-surface px-4 py-2 font-body-md text-body-md text-inverse-on-surface shadow-lg sm:left-auto md:bottom-6"
        >
          {toast}
          <button
            type="button"
            onClick={() => setToast(null)}
            className="ml-3 underline"
            aria-label="Dismiss"
          >
            close
          </button>
        </div>
      )}
    </div>
  );
}

function GuestPassTable({
  active,
  scheduled,
  expired,
  onRevoke,
}: {
  active: GuestPassRow[];
  scheduled: GuestPassRow[];
  expired: GuestPassRow[];
  onRevoke: (row: GuestPassRow) => void;
}) {
  function row(gp: GuestPassRow, revokable: boolean) {
    const s = statusOf(gp);
    return (
      <tr key={gp.id} className="bg-surface">
        <td className="px-4 py-3">
          <div className="font-title-sm text-title-sm text-on-surface">{gp.guest_name}</div>
          {gp.guest_email && (
            <div className="text-on-surface-variant">{gp.guest_email}</div>
          )}
        </td>
        <td className="px-4 py-3 text-on-surface-variant">
          {new Date(gp.valid_from).toLocaleString()}
        </td>
        <td className="px-4 py-3 text-on-surface-variant">
          {new Date(gp.valid_until).toLocaleString()}
        </td>
        <td className="px-4 py-3">
          <Badge tone={s.tone}>{s.label}</Badge>
          {/* The reason a pass is not usable matters more than the label. */}
          {s.detail ? (
            <div className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
              {s.detail}
            </div>
          ) : null}
        </td>
        <td className="px-4 py-3 text-right">
          {revokable ? (
            <Button variant="ghost" size="sm" onClick={() => onRevoke(gp)}>
              Revoke
            </Button>
          ) : (
            <span className="font-label-md text-label-md text-on-surface-variant">—</span>
          )}
        </td>
      </tr>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-outline-variant">
      <table className="w-full text-left font-body-md text-body-md">
        <thead className="bg-surface-container-low text-label-md font-label-md uppercase text-on-surface-variant">
          <tr>
            <th className="px-4 py-3">Guest</th>
            <th className="px-4 py-3">Valid from</th>
            <th className="px-4 py-3">Valid until</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-outline-variant">
          {active.map((gp) => row(gp, true))}
          {scheduled.map((gp) => row(gp, true))}
          {expired.map((gp) => row(gp, false))}
        </tbody>
      </table>
    </div>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-scrim/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface-container-high p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="font-title-lg text-title-lg text-on-surface">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-on-surface-variant hover:text-on-surface"
            aria-label="Close modal"
          >
            <Icon name="close" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
